import clsx from 'clsx';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ATTACHMENT_MAX_BYTES, ATTACHMENTS_PER_TARGET, type AttachmentDTO } from '@wd/shared';
import { attachmentUrl, fmtSize, isImage } from '../lib/attachments';
import { Icon } from './icons';
import './attachments.css';

const isVideo = (mime: string) => mime === 'video/mp4' || mime === 'video/webm';
/** 在页面里预览的类型：图片和视频；其他（PDF、文档）在新标签页打开或下载 */
const previewable = (f: AttachmentDTO) => isImage(f.mime) || isVideo(f.mime);

/** 附件列表：图片显示缩略图，点开在页面里预览（可关闭、左右切换）；其他文件显示名称和大小，在新标签页打开 */
export function AttachmentList({ files, onRemove, className }: { files: AttachmentDTO[]; onRemove?: (id: string) => void; className?: string }) {
  const [viewing, setViewing] = useState<number | null>(null);
  if (!files.length) return null;
  const media = files.filter(previewable);
  return (
    <div className={clsx('wd-files', className)} onClick={(e) => e.stopPropagation()}>
      {files.map((f) => (
        <span key={f.id} className={clsx('wd-file', isImage(f.mime) && 'img')}>
          <a
            href={attachmentUrl(f.id)}
            target="_blank"
            rel="noopener noreferrer"
            title={`${f.name}（${fmtSize(f.size)}）`}
            onClick={(e) => {
              // 图片和视频在页面里预览；按住 ⌘ / Ctrl 点仍然在新标签页打开
              if (!previewable(f) || e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              setViewing(media.indexOf(f));
            }}
          >
            {isImage(f.mime) ? (
              <img src={attachmentUrl(f.id)} alt={f.name} loading="lazy" />
            ) : (
              <>
                <Icon name="clip" size={13} />
                <span className="name">{f.name}</span>
                <span className="size">{fmtSize(f.size)}</span>
              </>
            )}
          </a>
          {onRemove && (
            <button type="button" className="rm" aria-label={`删除 ${f.name}`} onClick={() => onRemove(f.id)}>
              <Icon name="close" size={10} stroke={1.8} />
            </button>
          )}
        </span>
      ))}
      {viewing != null && media[viewing] && <Lightbox files={media} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** 全屏预览：× / Esc / 点空白处关闭，← → 切换，可下载或在新标签页打开 */
function Lightbox({ files, index, onIndex, onClose }: { files: AttachmentDTO[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const f = files[index]!;
  const many = files.length > 1;
  const go = (d: number) => onIndex((index + d + files.length) % files.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (many && e.key === 'ArrowLeft') go(-1);
      else if (many && e.key === 'ArrowRight') go(1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    // 预览时页面不滚动
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
    };
  });

  return createPortal(
    <div className="wd-lightbox" role="dialog" aria-modal="true" aria-label={f.name} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wd-lb-bar">
        <span className="name">
          {f.name}
          <span className="meta">
            {fmtSize(f.size)}
            {many ? ` · ${index + 1} / ${files.length}` : ''}
          </span>
        </span>
        <a className="wd-lb-btn" href={attachmentUrl(f.id)} download={f.name} title="下载">
          <Icon name="download" size={16} color="currentColor" />
          <span>下载</span>
        </a>
        <a className="wd-lb-btn" href={attachmentUrl(f.id)} target="_blank" rel="noopener noreferrer" title="在新标签页打开">
          <Icon name="link" size={16} color="currentColor" />
        </a>
        <button type="button" className="wd-lb-btn close" onClick={onClose} aria-label="关闭预览（Esc）" title="关闭（Esc）">
          <Icon name="close" size={18} color="currentColor" />
        </button>
      </div>
      <div className="wd-lb-stage" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {isVideo(f.mime) ? (
          <video key={f.id} src={attachmentUrl(f.id)} controls autoPlay playsInline />
        ) : (
          <img key={f.id} src={attachmentUrl(f.id)} alt={f.name} />
        )}
      </div>
      {many && (
        <>
          <button type="button" className="wd-lb-nav prev" onClick={() => go(-1)} aria-label="上一张">
            <Icon name="chevL" size={22} color="currentColor" />
          </button>
          <button type="button" className="wd-lb-nav next" onClick={() => go(1)} aria-label="下一张">
            <Icon name="chevR" size={22} color="currentColor" />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}

/** 选文件的按钮（隐藏的 input），可以多选 */
export function AttachButton({
  onFiles,
  count = 0,
  busy,
  progress,
  children,
  className,
}: {
  onFiles: (files: File[]) => void;
  count?: number;
  busy?: boolean;
  /** 上传进度 0–100 */
  progress?: number | null;
  children?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const full = count >= ATTACHMENTS_PER_TARGET;
  return (
    <>
      <button
        type="button"
        className={clsx('wd-attach-btn', className)}
        disabled={busy || full}
        title={full ? `最多 ${ATTACHMENTS_PER_TARGET} 个附件` : `添加附件（图片、视频、PDF、文档，单个不超过 ${Math.round(ATTACHMENT_MAX_BYTES / 1024 / 1024)} MB）`}
        onClick={(e) => {
          e.stopPropagation();
          ref.current?.click();
        }}
      >
        <Icon name="clip" size={14} />
        {busy ? `上传中${progress != null ? ` ${progress}%` : '…'}` : (children ?? '附件')}
      </button>
      <input
        ref={ref}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const list = [...(e.target.files ?? [])].slice(0, ATTACHMENTS_PER_TARGET - count);
          e.target.value = '';
          if (list.length) onFiles(list);
        }}
      />
    </>
  );
}
